import { STATUS } from "@/constants/statuses";
import { REWARDS_REWARD_TYPES } from "@apps/rewards/constants/rewardsRewardTypes";
import { filterNilValue } from "@datorama/akita";
import { CartModule } from "@modules/cart/CartModule";
import type { CartQuery } from "@modules/cart/queries/CartQuery";
import type { CartService } from "@modules/cart/services/CartService";
import { CommonModule } from "@modules/common_module/CommonModule";
import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
import type ShopifyDiscountQuery from "@modules/discount/query/ShopifyDiscountQuery";
import type { DiscountService } from "@modules/discount/service/DiscountService";
import { ShopifyDiscountModule } from "@modules/discount/ShopifyDiscountModule";
import Cookies from "js-cookie";
import type { Observable } from "rxjs";
import {
    BehaviorSubject,
    combineLatest,
    filter,
    lastValueFrom,
    map,
    skip,
    switchMap,
    tap,
} from "rxjs";
import { singleton } from "tsyringe";

import type { CustomerDiscountCodeRewardEntity } from "../models/RewardsCustomerRewardsEntities";
import RewardsCustomerActiveRewardsQuery from "../queries/RewardsCustomerActiveRewardsQuery";

const DISCOUNT_COOKIE_KEY = "discount_code";

@singleton()
class RewardsCustomerRewardsDiscountService {
    private readonly cartQuery: CartQuery;
    private readonly cartService: CartService;

    private readonly shopifyDiscountApiService: DiscountService;
    private readonly shopifyDiscountQuery: ShopifyDiscountQuery;

    private readonly locationController: LocationControllerInterface;
    private readonly urlsBuilder: URLsBuilder;

    private readonly checkoutItem$ = new BehaviorSubject<{
        variantId: number;
        rewardItemId: number | string;
    }>({
        variantId: 0,
        rewardItemId: 0,
    });

    private readonly cartStatus$ = this.checkoutItem$.pipe(
        filterNilValue(),
        switchMap((checkoutItem) => {
            return this.cartQuery.selectEditItemStatus(checkoutItem.variantId);
        })
    );

    constructor(
        private readonly customerActiveRewardsQuery: RewardsCustomerActiveRewardsQuery,
        private readonly cartModule: CartModule,
        private readonly shopifyDiscountModule: ShopifyDiscountModule,
        private readonly commonModule: CommonModule
    ) {
        const { shopifyDiscountApiService, shopifyDiscountQuery } =
            this.shopifyDiscountModule.exports;
        this.shopifyDiscountQuery = shopifyDiscountQuery;
        this.shopifyDiscountApiService = shopifyDiscountApiService;

        const { cartService, cartQuery } = this.cartModule.exports;
        this.cartQuery = cartQuery;
        this.cartService = cartService;

        const { locationController, urlsBuilder } = this.commonModule.exports;
        this.locationController = locationController;
        this.urlsBuilder = urlsBuilder;
    }

    private getDiscountFromCookie(): string | undefined {
        return Cookies.get(DISCOUNT_COOKIE_KEY);
    }

    public isDiscountApplied(discountCode: string): boolean {
        return this.getDiscountFromCookie() === discountCode;
    }

    public applyDiscount = async (
        discountCode: string,
        rewardItemId: number | string
    ) => {
        await lastValueFrom(
            this.shopifyDiscountApiService.applyDiscount(
                discountCode,
                rewardItemId
            )
        );
    };

    public applyDiscountAndAddToCard = async (
        variantId: number,
        discountCode: string,
        rewardItemId: number | string,
        isCheckout = true
    ) => {
        this.checkoutItem$.next({ variantId, rewardItemId });

        await this.applyDiscount(discountCode, rewardItemId);
        await this.cartService.addToCart([
            {
                variantID: variantId,
                quantity: 1,
            },
        ]);

        if (isCheckout) {
            this.locationController.goTo(this.urlsBuilder.createCartUrl());
        }
    };

    public selectCheckoutStatus(): Observable<Record<number, boolean>> {
        return combineLatest([
            this.checkoutItem$,
            this.cartStatus$,
            this.shopifyDiscountQuery.discountStatus$,
        ]).pipe(
            map(([checkoutItem, cartStatus, discountStatus]) => {
                return {
                    [checkoutItem.rewardItemId]:
                        discountStatus[checkoutItem.rewardItemId] ===
                            STATUS.LOADING || cartStatus === STATUS.LOADING,
                };
            })
        );
    }

    public applyFirstDiscount(): Observable<
        CustomerDiscountCodeRewardEntity | undefined
    > {
        return this.customerActiveRewardsQuery.customerActiveRewardsList$.pipe(
            skip(1),
            filter(
                (rewardsList) =>
                    rewardsList.length > 0 && !this.getDiscountFromCookie()
            ),
            map((rewardsList) =>
                rewardsList.find(
                    (item): item is CustomerDiscountCodeRewardEntity =>
                        item.reward.type === REWARDS_REWARD_TYPES.discount
                )
            ),
            tap((item) => {
                if (item) {
                    void this.applyDiscount(item.reward.discountCode, item.id);
                }
            })
        );
    }
}

export default RewardsCustomerRewardsDiscountService;
